import { useState } from 'react';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';

const PRICING_MODELS = [
  { key: 'fixed_price', label: 'Fixed Price' },
  { key: 'market_indexed', label: 'Market Indexed' },
  { key: 'formula_based', label: 'Formula Based' },
  { key: 'feed_in_tariff', label: 'Feed-in Tariff' },
  { key: 'custom', label: 'Custom' },
];

interface FinancialTerm {
  id: string;
  currency: string;
  pricingModel: string;
  priceFormula?: string;
  paymentTerms: string;
  settlementPeriod: string;
}

interface Props {
  assetId: string;
  terms?: FinancialTerm[];
  onSaved: () => void;
}

const PRICING_LABELS: Record<string, string> = {
  fixed_price: 'Fixed Price',
  market_indexed: 'Market Indexed',
  formula_based: 'Formula Based',
  feed_in_tariff: 'Feed-in Tariff',
  custom: 'Custom',
};

export default function TabFinancial({ assetId, terms = [], onSaved }: Props) {
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(terms.length === 0);
  const [form, setForm] = useState({
    currency: 'UAH',
    pricingModel: '',
    priceFormula: '',
    paymentTerms: '',
    settlementPeriod: '',
  });

  const handleAdd = async () => {
    if (!form.currency || !form.pricingModel) {
      addToast('Currency and pricing model are required', 'warning');
      return;
    }
    setSaving(true);
    setApiError(null);
    try {
      const payload: Record<string, unknown> = {
        currency: form.currency,
        pricingModel: form.pricingModel,
      };
      if (form.priceFormula) payload.priceFormula = form.priceFormula;
      if (form.paymentTerms) payload.paymentTerms = form.paymentTerms;
      if (form.settlementPeriod) payload.settlementPeriod = form.settlementPeriod;

      await api.post(`/assets/${assetId}/financial-terms`, payload);
      addToast('Financial terms added successfully', 'success');
      setForm({ currency: 'UAH', pricingModel: '', priceFormula: '', paymentTerms: '', settlementPeriod: '' });
      setShowForm(false);
      onSaved();
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to add financial terms');
      setApiError(message);
      addToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? '#1A1A1A' : '#fff';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const sectionBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const sectionBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const inputClass = 'w-full px-3 py-2 rounded-md text-sm outline-none transition-colors';
  const inputStyle = { background: inputBg, border: `1px solid ${inputBorder}`, color: textMain };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(209,34,41,0.08)' }}>
            <i className="ri-money-dollar-circle-line text-sm" style={{ color: '#D12229' }}></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: textMain }}>Financial Terms</h3>
            <p className="text-xs" style={{ color: '#6B6B70' }}>Pricing model, payment terms and settlement periods</p>
          </div>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
            style={{ border: '1px solid #D12229', color: '#D12229' }}
          >
            <i className="ri-add-line"></i> Add Terms
          </button>
        )}
      </div>

      {/* Existing terms */}
      {terms.length > 0 && (
        <div className="space-y-2 mb-5">
          {terms.map((ft) => (
            <div key={ft.id} className="p-4 rounded-lg" style={{ border: '1px solid #E5E5E8' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium" style={{ color: textMain }}>
                    {PRICING_LABELS[ft.pricingModel] ?? ft.pricingModel}
                  </span>
                </div>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: '#F5F5F7', color: '#1A1A1A' }}>
                  {ft.currency}
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs" style={{ color: '#6B6B70' }}>
                {ft.paymentTerms && <span><i className="ri-calendar-check-line mr-1"></i>{ft.paymentTerms}</span>}
                {ft.settlementPeriod && <span><i className="ri-time-line mr-1"></i>{ft.settlementPeriod}</span>}
              </div>
              {ft.priceFormula && (
                <div className="mt-2 px-3 py-2 rounded-md text-xs font-mono" style={{ background: '#F5F5F7', color: '#1A1A1A' }}>
                  {ft.priceFormula}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div className="p-5 rounded-xl" style={{ background: '#F9F9FB', border: '1px solid #E5E5E8' }}>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold" style={{ color: textMain }}>New Financial Terms</h4>
            {terms.length > 0 && (
              <button onClick={() => setShowForm(false)} className="text-xs cursor-pointer" style={{ color: '#6B6B70' }}>
                Cancel
              </button>
            )}
          </div>

          {apiError && (
            <div className="mb-4 px-4 py-3 rounded-lg text-sm" style={{ background: 'rgba(209,34,41,0.06)', border: '1px solid rgba(209,34,41,0.2)', color: '#D12229' }}>
              <i className="ri-error-warning-line mr-2"></i>{apiError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B6B70' }}>
                Currency <span style={{ color: '#D12229' }}>*</span>
              </label>
              <select
                value={form.currency}
                onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value }))}
                className={inputClass}
                style={inputStyle}
              >
                <option value="UAH">UAH — Ukrainian Hryvnia</option>
                <option value="EUR">EUR — Euro</option>
                <option value="USD">USD — US Dollar</option>
                <option value="PLN">PLN — Polish Zloty</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B6B70' }}>
                Pricing Model <span style={{ color: '#D12229' }}>*</span>
              </label>
              <select
                value={form.pricingModel}
                onChange={(e) => setForm((prev) => ({ ...prev, pricingModel: e.target.value }))}
                className={inputClass}
                style={inputStyle}
              >
                <option value="">Select model...</option>
                {PRICING_MODELS.map((m) => (
                  <option key={m.key} value={m.key}>{m.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B6B70' }}>Payment Terms</label>
              <input
                type="text"
                value={form.paymentTerms}
                onChange={(e) => setForm((prev) => ({ ...prev, paymentTerms: e.target.value }))}
                placeholder="e.g. Net 30, Monthly"
                className={inputClass}
                style={inputStyle}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B6B70' }}>Settlement Period</label>
              <input
                type="text"
                value={form.settlementPeriod}
                onChange={(e) => setForm((prev) => ({ ...prev, settlementPeriod: e.target.value }))}
                placeholder="e.g. Monthly, Quarterly"
                className={inputClass}
                style={inputStyle}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B6B70' }}>
                Price Formula
                <span className="ml-1 font-normal" style={{ color: '#6B6B70' }}>(for formula-based pricing)</span>
              </label>
              <input
                type="text"
                value={form.priceFormula}
                onChange={(e) => setForm((prev) => ({ ...prev, priceFormula: e.target.value }))}
                placeholder="e.g. DAM_price * 0.95 + 50"
                className={inputClass}
                style={inputStyle}
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={handleAdd}
              disabled={saving || !form.currency || !form.pricingModel}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium text-white cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
              style={{ background: '#D12229' }}
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i> Adding...</>
              ) : (
                <><i className="ri-add-line"></i> Add Terms</>
              )}
            </button>
          </div>
        </div>
      )}

      {terms.length === 0 && !showForm && (
        <div className="text-center py-8">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: '#F5F5F7' }}>
            <i className="ri-money-dollar-circle-line text-xl" style={{ color: '#6B6B70' }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>No financial terms defined</p>
          <p className="text-xs mt-1" style={{ color: '#6B6B70' }}>Add pricing model and payment conditions</p>
        </div>
      )}
    </div>
  );
}
